import "url-search-params-polyfill"; // polyfill for URLSearchParams on IE11
import { State } from "data";
import { getUUIDByName } from "data/form/elementSelectors";
import { newForm, removeForm, statusReady } from "data/form/formActions";
import api from "lib/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getStatus } from "../data/form/formSelectors";
import { getErrorMessageOrDefault } from "lib/api/errors";
import { createAlertAction } from "data/app/appActions";
import { createAlert } from "records/Alert";
import { useTranslation } from "react-i18next";

export default (fid: string, linkIndex: number) => {
	const history = useHistory();
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const [init, setInit] = useState(false);
	const [child, setChild] = useState<string>();

	// Errors that occur before the form instance exists, this would likely
	// indicate an issue in the form definition
	const [initError, setInitError] = useState(false);

	const hasChild = useMemo(() => {
		const linked = new URLSearchParams(history.location.search).get(
			"linked"
		);
		if (linked) {
			const parseLinked = JSON.parse(linked);
			if (
				parseLinked.length > 0 &&
				parseLinked[linkIndex] &&
				parseLinked[linkIndex].bindingIndex !== undefined &&
				parseLinked[linkIndex].name !== undefined
			)
				return parseLinked[linkIndex];
		}
		return false;
	}, [history.location.search, linkIndex]);

	// Once the config details have been set, we can get the UUID using the name of the element from redux
	const childUUID = useSelector((state: State) => {
		if (hasChild) {
			return getUUIDByName(state, fid, hasChild.name);
		}
	});

	const childStatus = useSelector((state: State) => {
		if (child) return getStatus(state, { fid: child });
	});

	const loadChild = useCallback(() => {
		// Load the child
		if (hasChild && childUUID) {
			setInit(true);

			api.forms
				.createChild(
					fid,
					{
						element: childUUID,
						bindingIndex: hasChild.bindingIndex,
						index: hasChild.index,
					},
					hasChild.newRecord,
					{ handleErrors: true }
				)
				.then(form => {
					dispatch(newForm(form.instanceId, form));
					dispatch(statusReady(form.instanceId));
					setChild(form.instanceId);
				})
				.catch(error => {
					dispatch(
						createAlertAction(
							createAlert({
								title: t("alerts.initErrorTitle"),
								status: "error",
								description: getErrorMessageOrDefault(
									error.response
								),
								timeout: 10000,
							})
						)
					);
					setInitError(true);
				});
		}
	}, [childUUID, dispatch, fid, hasChild, t]);

	const removeChild = useCallback(() => {
		if (child) dispatch(removeForm(child));

		setInit(false);
		setChild(undefined);
	}, [child, dispatch]);

	useEffect(() => {
		if ((childStatus === "CLOSED" && init) || (initError && init)) {
			let otherParams = "";
			let links;
			const searchParams = new URLSearchParams(history.location?.search);

			// Iterate the search parameters
			for (const p of searchParams) {
				p[0] === "linked"
					? (links = p[1])
					: (otherParams += `&${p[0]}=${p[1]}`);
			}

			if (links) {
				const update = JSON.parse(links);
				update.pop();

				if (update.length > 0) {
					history.push({
						pathname: history.location.pathname,
						search: `?linked=${JSON.stringify(
							update
						)}${otherParams}`,
					});
				} else {
					history.push({
						pathname: history.location.pathname,
						search: `?${otherParams}`,
					});
				}
			}
			removeChild();
			setInitError(false);
		}
	}, [childStatus, history, init, initError, removeChild]);

	return {
		init,
		hasChild,
		loadChild,
		child,
	};
};
